// Voice selection for SpeechSynthesis Web API.
// The Web Speech API does not expose gender directly, so we use heuristics based on
// common patterns in voice names across Chrome, Edge, Safari, and OS-level voices.

const FEMALE_PATTERNS = [
  // Generic
  /female/i, /femin(a|e|in)/i,
  // PT-BR / PT
  /\b(maria|luciana|francisca|joana|helena|camila|fernanda|paula|amélia|amelia|catarina|joaquina)\b/i,
  // EN
  /\b(samantha|victoria|karen|fiona|moira|tessa|veena|kate|serena|allison|ava|susan|zoe|zira|hazel|eva)\b/i,
  // ES
  /\b(monica|paulina|esperanza|marisol|helena|catalina)\b/i,
  // FR
  /\b(amelie|amélie|audrey|virginie|julie)\b/i,
  // IT
  /\b(alice|elsa|luca-?female|federica)\b/i,
  // DE
  /\b(anna|petra|katja|hedda|stefan-?fem)\b/i,
];

const MALE_PATTERNS = [
  /\bmale\b/i, /masculin/i,
  // PT-BR / PT
  /\b(daniel|felipe|joao|joão|antonio|antônio|paulo|pedro|carlos|ricardo|fabio|fábio)\b/i,
  // EN
  /\b(alex|fred|tom|david|mark|james|aaron|albert|bruce|daniel|george|guy|jorge|oliver|ralph|reed|rishi|gordon)\b/i,
  // ES
  /\b(diego|jorge|carlos|juan|pablo|raul|raúl|miguel)\b/i,
  // FR
  /\b(thomas|nicolas|paul|henri|claude)\b/i,
  // IT
  /\b(luca|paolo|giorgio)\b/i,
  // DE
  /\b(stefan|markus|hans|conrad)\b/i,
];

const isFemale = (name) => FEMALE_PATTERNS.some((re) => re.test(name));
const isMale = (name) => MALE_PATTERNS.some((re) => re.test(name));

/**
 * Returns voices filtered for the requested language.
 * Matches by exact lang first ("pt-BR"), falls back to language prefix ("pt").
 */
export const getVoicesForLang = (allVoices, lang) => {
  if (!allVoices || allVoices.length === 0) return [];
  const exact = allVoices.filter((v) => v.lang === lang);
  if (exact.length) return exact;
  const prefix = lang.split("-")[0];
  return allVoices.filter((v) => v.lang.toLowerCase().startsWith(prefix.toLowerCase()));
};

/**
 * Detect probable gender of a voice by name.
 * Returns 'female' | 'male' | 'unknown'.
 */
export const detectVoiceGender = (voice) => {
  if (!voice || !voice.name) return "unknown";
  const name = voice.name;
  // Microsoft Edge voices: "Microsoft Maria Online (Natural) - Portuguese (Brazil)"
  if (isFemale(name)) return "female";
  if (isMale(name)) return "male";
  return "unknown";
};

/**
 * Picks the best voice for a given language + gender preference + optional explicit voiceURI.
 *
 * Priority:
 *  1. Exact match by voiceURI (user explicitly picked a voice)
 *  2. Voice in target language with matching gender
 *  3. Voice in target language with unknown gender (fallback if no gendered match)
 *  4. Any voice in target language
 *  5. null → browser default
 */
export const pickVoice = (allVoices, { lang, gender, voiceURI } = {}) => {
  if (!allVoices || allVoices.length === 0) return null;

  if (voiceURI) {
    const found = allVoices.find((v) => v.voiceURI === voiceURI);
    if (found) return found;
  }

  const langVoices = getVoicesForLang(allVoices, lang || "pt-BR");
  if (langVoices.length === 0) return null;

  if (gender === "female" || gender === "male") {
    const target = gender;
    const match = langVoices.find((v) => detectVoiceGender(v) === target);
    if (match) return match;
    // Fallback: prefer one that's NOT the opposite gender (i.e. unknown)
    const opposite = target === "female" ? "male" : "female";
    const notOpposite = langVoices.find((v) => detectVoiceGender(v) !== opposite);
    if (notOpposite) return notOpposite;
  }

  return langVoices[0];
};

/**
 * Returns a list of voices ready to render in a Select, with metadata.
 * Each item: { voiceURI, name, lang, gender, label }
 */
export const listVoicesForUI = (allVoices, lang) => {
  const filtered = getVoicesForLang(allVoices, lang);
  return filtered.map((v) => {
    const gender = detectVoiceGender(v);
    const genderLabel = gender === "female" ? "♀" : gender === "male" ? "♂" : "·";
    return {
      voiceURI: v.voiceURI,
      name: v.name,
      lang: v.lang,
      gender,
      label: `${genderLabel}  ${v.name}  (${v.lang})`,
    };
  });
};
